import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React from 'react';

type TableCellData =
  | React.ReactNode
  | {
      content: React.ReactNode;
      props?: TableCellProps;
    };

type ColumnDef<TData = unknown> = {
  header: TableCellData;
  renderCell: (row: TData, rowIndex: number) => TableCellData;
  headerProps?: TableCellProps;
  cellProps?: (row: TData, rowIndex: number) => TableCellProps | undefined;
};

type SimpleTableProps<TData = unknown> =
  | {
      // New API: columns and data
      columns: ColumnDef<TData>[];
      data: readonly TData[] | TData[];
      headers?: never;
      rows?: never;
    }
  | {
      // Old API: headers and rows (backwards compatible)
      headers: TableCellData[];
      rows: TableCellData[][];
      columns?: never;
      data?: never;
    };

export function SimpleTable<TData = unknown>(props: SimpleTableProps<TData>) {
  const renderCell = (data: TableCellData, index: number, additionalProps?: TableCellProps) => {
    if (
      typeof data === 'object' &&
      data !== null &&
      'content' in data &&
      !React.isValidElement(data)
    ) {
      const { content, props: cellProps } = data as {
        content: React.ReactNode;
        props?: TableCellProps;
      };
      return (
        <TableCell key={index} {...additionalProps} {...cellProps}>
          {content}
        </TableCell>
      );
    }
    return (
      <TableCell key={index} {...additionalProps}>
        {data}
      </TableCell>
    );
  };

  // Check if using new API (columns/data) or old API (headers/rows)
  if ('columns' in props && props.columns && 'data' in props && props.data) {
    // New API: columns and data
    const { columns, data } = props;

    const renderHeader = (column: ColumnDef<TData>, index: number) => {
      const header = column.header;
      return renderCell(header, index, column.headerProps);
    };

    const renderCellContent = (
      column: ColumnDef<TData>,
      row: TData,
      rowIndex: number,
      colIndex: number,
    ) => {
      const cellResult = column.renderCell(row, rowIndex);
      const cellProps = column.cellProps?.(row, rowIndex);
      return renderCell(cellResult, colIndex, cellProps);
    };

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>{columns.map((column, index) => renderHeader(column, index))}</TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column, colIndex) =>
                  renderCellContent(column, row, rowIndex, colIndex),
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Old API: headers and rows (backwards compatible)
  const { headers, rows } = props;

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>{headers.map((header, index) => renderCell(header, index))}</TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {row.map((data, cellIndex) => renderCell(data, cellIndex))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
